import {
  PositionStatusReason,
  PositionStatusReasonCantLiquidatePosAboveMMR,
  PositionStatusReasonIncreasePositionCollateral,
  PositionStatusReasonInsufficientFundsToDecCollateral,
  Deprecated_PositionStatusReasonInsurancePaymentForLiquidation,
  PositionStatusReasonInsurancePaymentForSettlement,
  PositionStatusReasonInvalidBankruptcyPrice,
  PositionStatusReasonInvalidLiquidationPrice,
  PositionStatusReasonPositionClosed,
  PositionStatusReasonPositionDecreased,
  PositionStatusReasonPositionDoesNotExist,
  PositionStatusReasonPositionIncreased,
  PositionStatusReasonPositionInverted,
  PositionStatusReasonPositionLiquidated,
  PositionStatusReasonPositionOpened,
  PositionStatusReasonUnspecified,
} from '@/api/spec';
import { Side } from '@/common/constants';
import { PositionStatus, ViewPosition } from './types';

type NotificationSeverity = 'success' | 'info' | 'warning' | 'error';

interface PositionReasonInfo {
  message: string;
  severity: NotificationSeverity;
}

const reasonInfoMap: Partial<Record<PositionStatusReason, PositionReasonInfo>> = {
  [PositionStatusReasonUnspecified]: {
    message: 'was updated',
    severity: 'info',
  },
  [PositionStatusReasonCantLiquidatePosAboveMMR]: {
    message: 'could not be liquidated because margin requirements were met',
    severity: 'warning',
  },
  [PositionStatusReasonIncreasePositionCollateral]: {
    message: 'collateral was added',
    severity: 'success',
  },
  [PositionStatusReasonInsufficientFundsToDecCollateral]: {
    message: 'collateral update failed due to insufficient funds',
    severity: 'error',
  },
  [Deprecated_PositionStatusReasonInsurancePaymentForLiquidation]: {
    message: 'was liquidated and covered by the insurance fund',
    severity: 'warning',
  },
  [PositionStatusReasonInsurancePaymentForSettlement]: {
    message: 'was settled using the insurance fund. Check your account balance for the settled amount',
    severity: 'warning',
  },
  [PositionStatusReasonInvalidBankruptcyPrice]: {
    message: 'could not be processed due to an invalid bankruptcy price',
    severity: 'error',
  },
  [PositionStatusReasonInvalidLiquidationPrice]: {
    message: 'could not be processed due to an invalid liquidation price',
    severity: 'error',
  },
  [PositionStatusReasonPositionClosed]: {
    message: 'was closed',
    severity: 'success',
  },
  [PositionStatusReasonPositionDecreased]: {
    message: 'was reduced',
    severity: 'success',
  },
  [PositionStatusReasonPositionDoesNotExist]: {
    message: 'could not be found',
    severity: 'error',
  },
  [PositionStatusReasonPositionIncreased]: {
    message: 'size was increased',
    severity: 'info',
  },
  [PositionStatusReasonPositionInverted]: {
    message: 'was flipped to the opposite side',
    severity: 'warning',
  },
  [PositionStatusReasonPositionLiquidated]: {
    message: 'was liquidated. Deposit funds to open a new position',
    severity: 'warning',
  },
  [PositionStatusReasonPositionOpened]: {
    message: 'was opened',
    severity: 'success',
  },
};

const statusSeverityFallback: Partial<Record<PositionStatus, NotificationSeverity>> = {
  [PositionStatus.closed]: 'success',
  [PositionStatus.liquidated]: 'error',
  [PositionStatus.failed]: 'error',
  [PositionStatus.deleveraged]: 'warning',
  [PositionStatus.unwound]: 'warning',
};

const importantStatuses: ReadonlySet<PositionStatus> = new Set([
  PositionStatus.closed,
  PositionStatus.liquidated,
  PositionStatus.failed,
  PositionStatus.deleveraged,
  PositionStatus.unwound,
]);

const defaultMessageByStatus: Partial<Record<PositionStatus, string>> = {
  [PositionStatus.closed]: 'was closed',
  [PositionStatus.liquidated]: 'was liquidated',
  [PositionStatus.failed]: 'failed',
  [PositionStatus.deleveraged]: 'was deleveraged',
  [PositionStatus.unwound]: 'was unwound',
};

const formatSize = (size?: number, decimals?: number): string => {
  if (!size || Number.isNaN(size)) return '';
  const precision = Math.min(Math.max(decimals ?? 4, 0), 6);
  return Number(size.toFixed(precision)).toString();
};

const buildPositionBaseMessage = (position: ViewPosition): string => {
  const symbol = position.assetCurrency || `Market ${position.marketId}`;
  const sideText = position.side === Side.long ? 'long' : 'short';
  const sizeText = position.size ? ` size ${formatSize(position.size, position.assetDecimals)}` : '';
  return `${symbol} ${sideText} position${sizeText}`.trim();
};

const getReasonInfo = (reason?: PositionStatusReason): PositionReasonInfo | undefined => {
  if (reason == null) return undefined;
  return reasonInfoMap[reason];
};

export interface PositionNotification {
  message: string;
  severity: NotificationSeverity;
  debugInfo?: string;
}

const notifyAlwaysReasons: ReadonlySet<PositionStatusReason> = new Set([
  PositionStatusReasonIncreasePositionCollateral,
  PositionStatusReasonPositionDecreased,
]);

const terminalPnlStatuses: ReadonlySet<PositionStatus> = new Set([
  PositionStatus.closed,
  PositionStatus.liquidated,
  PositionStatus.deleveraged,
  PositionStatus.unwound,
]);

const pnlFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  signDisplay: 'always',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const buildPositionNotification = (
  position: ViewPosition,
  previousStatus?: PositionStatus
): PositionNotification | null => {
  const isNotifyAlways = position.statusReason && notifyAlwaysReasons.has(position.statusReason);

  if (previousStatus === position.status && !isNotifyAlways) {
    return null;
  }

  if (!importantStatuses.has(position.status) && !isNotifyAlways) {
    return null;
  }

  const baseMessage = buildPositionBaseMessage(position);
  const reasonInfo = getReasonInfo(position.statusReason);
  const suffix = reasonInfo?.message ?? defaultMessageByStatus[position.status] ?? 'status changed';
  const severity = reasonInfo?.severity ?? statusSeverityFallback[position.status] ?? 'info';
  const pnlSuffix =
    terminalPnlStatuses.has(position.status) && position.realizedPnl != null
      ? `, PnL ${pnlFormatter.format(position.realizedPnl)}`
      : '';
  const message = `${baseMessage} ${suffix}${pnlSuffix}`.trim();
  const debugInfo = `Position ID: ${position.positionId}, Market ID: ${position.marketId}`;

  return {
    message,
    severity,
    debugInfo: severity === 'error' || severity === 'warning' ? debugInfo : undefined,
  };
};

export function getPositionErrorInfo(reason: PositionStatusReason, context?: string): PositionReasonInfo {
  const info = { ...(reasonInfoMap[reason] || reasonInfoMap[PositionStatusReasonUnspecified]!) };

  if (context) {
    info.message = `${info.message} (${context})`;
  }

  return info;
}
